import React from "react";
import { GripVertical, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  PostCheckoutFeedbackReminder,
  PostCheckoutFeedbackReminderCondition,
} from "@/types/booking-checkout-settings";

interface FeedbackReminderCardProps {
  reminder: PostCheckoutFeedbackReminder;
  canRemove: boolean;
  onChange: (reminder: PostCheckoutFeedbackReminder) => void;
  onRemove: () => void;
}

const sendAfterOptions = [1, 2, 3, 4, 6, 12, 24, 48];

const conditionOptions: PostCheckoutFeedbackReminderCondition[] = [
  "EMAIL_OPENED",
  "EMAIL_NOT_OPENED",
];

export const FeedbackReminderCard = ({
  reminder,
  canRemove,
  onChange,
  onRemove,
}: FeedbackReminderCardProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <GripVertical className="size-4 cursor-grab active:cursor-grabbing" />
          {t("feedbacks.settings.form.reminderTitle", {
            order: reminder.order,
          })}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="icon" disabled>
            <Pencil className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={!canRemove}
            onClick={onRemove}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <Label className="flex flex-col gap-2 text-sm font-medium">
          {t("feedbacks.settings.form.sendAfter")}
          <Select
            value={String(reminder.sendAfterHours)}
            onValueChange={(value) =>
              onChange({
                ...reminder,
                sendAfterHours: Number(value),
              })
            }
          >
            <SelectTrigger className="h-10 w-full justify-between">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {sendAfterOptions.map((hours) => (
                  <SelectItem key={hours} value={String(hours)}>
                    {t("feedbacks.settings.form.hours", { count: hours })}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Label>

        <Label className="flex flex-col gap-2 text-sm font-medium">
          {t("feedbacks.settings.form.condition")}
          <Select
            value={reminder.condition}
            onValueChange={(value) =>
              onChange({
                ...reminder,
                condition: value as PostCheckoutFeedbackReminderCondition,
              })
            }
          >
            <SelectTrigger className="h-10 w-full justify-between">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {conditionOptions.map((condition) => (
                  <SelectItem key={condition} value={condition}>
                    {t(`feedbacks.settings.form.conditions.${condition}`)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Label>
      </div>
    </div>
  );
};
